import React from 'react';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import {
    ClientDashboardData,
    PeriodType,
    ComparisonType
} from '../../../interfaces/dashboards/CrossClientDashboard.interface';
import CrossClientDashboardService from '../../../services/dashboards/CrossClientDashboard.service';

interface ClientCardProps {
    client: ClientDashboardData;
    periodType: PeriodType;
    comparisonType: ComparisonType;
    onViewClientProgress?: (clientId: number) => void | Promise<void>;
    isOpening?: boolean;
}

const ClientCard: React.FC<ClientCardProps> = ({ client, periodType, comparisonType, onViewClientProgress, isOpening = false }) => {
    const { clientName, clientId, metadata } = client;
    const metrics = CrossClientDashboardService.getSelectedMetrics(client, periodType, comparisonType);
    const salesComparisonPercent = CrossClientDashboardService.calculateComparisonPercent(metrics.sales);
    const inflowComparisonPercent = CrossClientDashboardService.calculateComparisonPercent(metrics.inflow);
    const inventoryComparisonPercent = CrossClientDashboardService.calculateComparisonPercent(metrics.inventory);

    // Dynamic labels based on period and comparison type
    const comparisonLabel = comparisonType === 'plan' ? 'plan' : 'ly';
    const comparisonUpperLabel = comparisonType === 'plan' ? 'Plan' : 'LY';
    const timePeriodLabel = periodType === 'current' ? 'MTD' : 'Last Month';
    const isCurrentMonth = periodType === 'current';
    const trendContextLabel = isCurrentMonth ? 'Projected' : 'Closed Month';
    const getVarianceCopy = (comparisonPercent: number) => {
        const direction = comparisonPercent >= 0 ? 'over' : 'under';
        const prefix = isCurrentMonth ? 'trending ' : '';

        return `${Math.abs(comparisonPercent).toFixed(1)}% ${prefix}${direction} ${comparisonLabel}`;
    };

    return (
        <div className="py-4 px-4 bg-white rounded border border-gray-300 overflow-hidden">
            <div className="grid grid-cols-4 gap-0">
                {/* Column 1 - Client Info & Metadata */}
                <div className="p-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-1">{clientName}</h3>

                    {/* View Client Progress Button */}
                    {onViewClientProgress && (
                        <button
                            onClick={() => onViewClientProgress(clientId)}
                            disabled={isOpening}
                            className="border-1 py-1 px-1 text-xs font-medium text-purple-700 hover:text-purple-900 hover:underline mb-3 inline-flex items-center gap-1 whitespace-nowrap"
                            type="button"
                        >
                            <span>{isOpening ? 'Opening Client Detail...' : 'View Client Detail'}</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-external-link h-3.5 w-3.5">
                                <path d="M15 3h6v6"></path>
                                <path d="M10 14 21 3"></path>
                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                            </svg>
                        </button>
                    )}

                    {/* Data Quality */}
                    <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-xs">
                            {metadata.unmappedClasses > 0 ? (
                                <>
                                    <AlertTriangle className="h-3 w-3 text-yellow-600" />
                                    <span className="text-yellow-700">{metadata.unmappedClasses} unmapped</span>
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                    <span className="text-green-700">All mapped</span>
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-2 text-xs">
                            {metadata.previousMonthStatus === 'Finalized' ? (
                                <>
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                    <span className="text-gray-700">Prev Month Finalized</span>
                                </>
                            ) : (
                                <>
                                    <Clock className="h-3 w-3 text-orange-500" />
                                    <span className="text-gray-700">Prev Month Pending</span>
                                </>
                            )}
                        </div>

                        <div className="flex items-center gap-2 text-xs">
                            {metadata.plansStatus === 'Built' ? (
                                <>
                                    <CheckCircle className="h-3 w-3 text-green-600" />
                                    <span className="text-gray-700">Plans Built</span>
                                </>
                            ) : (
                                <>
                                    <Clock className="h-3 w-3 text-orange-500" />
                                    <span className="text-gray-700">Plans Pending</span>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Last Updated */}
                    <div className="text-xs text-gray-500">
                        Last Updated<br />
                        <span className="text-gray-900">{CrossClientDashboardService.formatTimestamp(metadata.lastUpload)}</span>
                    </div>
                </div>

                {/* Column 2 - Sales (Green border) */}
                <div className="ml-4 p-4 border-l-4 border-l-green-500 bg-green-50/30">
                    <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">Sales</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${salesComparisonPercent >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {salesComparisonPercent >= 0 ? '+' : ''}{salesComparisonPercent.toFixed(1)}%
                        </span>
                    </div>
                    <div className="text-lg font-bold text-gray-900 mb-1">
                        {CrossClientDashboardService.moneyFormat(metrics.sales.actual)}
                        <span className="text-sm font-normal text-gray-600 ml-2">/ {comparisonUpperLabel}: {CrossClientDashboardService.moneyFormat(metrics.sales.comparison)}</span>
                    </div>
                    <div className="text-xs mb-3">
                        <span className={salesComparisonPercent >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                            {getVarianceCopy(salesComparisonPercent)}
                        </span>
                        <span className="text-gray-600"> • {timePeriodLabel}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white rounded p-2 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">Trended</div>
                            <div className="text-sm font-semibold text-gray-900">
                                {CrossClientDashboardService.moneyFormat(metrics.sales.trended)}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">{trendContextLabel}</div>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">{comparisonUpperLabel}</div>
                            <div className="text-sm font-semibold text-gray-900">
                                {CrossClientDashboardService.moneyFormat(metrics.sales.comparison)}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">Target</div>
                        </div>
                    </div>
                </div>

                {/* Column 3 - Inflow (Blue border) */}
                <div className="ml-4 p-4 border-l-4 border-l-blue-500 bg-blue-50/30">
                    <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">Inflow</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${inflowComparisonPercent >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {inflowComparisonPercent >= 0 ? '+' : ''}{inflowComparisonPercent.toFixed(1)}%
                        </span>
                    </div>
                    <div className="text-lg font-bold text-gray-900 mb-1">
                        {CrossClientDashboardService.moneyFormat(metrics.inflow.actual)}
                        <span className="text-sm font-normal text-gray-600 ml-2">/ {comparisonUpperLabel}: {CrossClientDashboardService.moneyFormat(metrics.inflow.comparison)}</span>
                    </div>
                    <div className="text-xs mb-3">
                        <span className={inflowComparisonPercent >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                            {getVarianceCopy(inflowComparisonPercent)}
                        </span>
                        <span className="text-gray-600"> • {timePeriodLabel}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white rounded p-2 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">Trended</div>
                            <div className="text-sm font-semibold text-gray-900">
                                {CrossClientDashboardService.moneyFormat(metrics.inflow.trended)}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">{trendContextLabel}</div>
                        </div>
                        <div className="bg-gray-50  rounded p-2 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">{comparisonUpperLabel}</div>
                            <div className="text-sm font-semibold text-gray-900">
                                {CrossClientDashboardService.moneyFormat(metrics.inflow.comparison)}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">Target</div>
                        </div>
                    </div>
                </div>

                {/* Column 4 - Inventory (Orange border) */}
                <div className="ml-4 p-4 border-l-4 border-l-orange-500 bg-orange-50/30">
                    <div className="flex items-start justify-between mb-2">
                        <span className="text-sm font-semibold text-gray-900">Inventory</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${inventoryComparisonPercent >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {inventoryComparisonPercent >= 0 ? '+' : ''}{inventoryComparisonPercent.toFixed(1)}%
                        </span>
                    </div>
                    <div className="text-lg font-bold text-gray-900 mb-1">
                        {CrossClientDashboardService.moneyFormat(metrics.inventory.actual)}
                        <span className="text-sm font-normal text-gray-600 ml-2">/ {comparisonUpperLabel}: {CrossClientDashboardService.moneyFormat(metrics.inventory.comparison)}</span>
                    </div>
                    <div className="text-xs mb-3">
                        <span className={inventoryComparisonPercent >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                            {getVarianceCopy(inventoryComparisonPercent)}
                        </span>
                        <span className="text-gray-600"> • {timePeriodLabel}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div className="bg-white rounded p-2 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">Trended</div>
                            <div className="text-sm font-semibold text-gray-900">
                                {CrossClientDashboardService.moneyFormat(metrics.inventory.trended)}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">{trendContextLabel}</div>
                        </div>
                        <div className="bg-gray-50 rounded p-2 border border-gray-200">
                            <div className="text-xs text-gray-500 mb-1">{comparisonUpperLabel}</div>
                            <div className="text-sm font-semibold text-gray-900">
                                {CrossClientDashboardService.moneyFormat(metrics.inventory.comparison)}
                            </div>
                            <div className="text-xs text-gray-500 mb-1">Target</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientCard;

